/**
 * SeeMe Overlay System
 * Lightweight vanilla JS for modals and sheets.
 *
 * THREE SHAPES, in order of how much the content costs:
 *
 * 1. STATIC — the overlay is already in the page.
 *      <button data-overlay-target="#my-modal">Open</button>
 *
 *    Right for a confirm dialog or a short form: markup that is cheap to render
 *    shut and needs no round trip. Wrong for anything with images, a map, a
 *    chart, or a long list — see the note below on what "hidden" costs.
 *
 * 2. LAZY FRAGMENT — the view returns body content; SeeMe supplies the chrome.
 *      <button data-overlay-url="/help/pricing/"
 *              data-overlay-title="How pricing works">Open</button>
 *
 *    The placeholder's header, close button and size class survive; only the
 *    body is filled. Right for help text, a policy blurb, a definition — one
 *    view, one `<p>`, no template boilerplate. The title comes from the
 *    trigger because the response has no header to put one in.
 *
 * 3. LAZY OVERLAY — the view returns the WHOLE element.
 *      <button data-overlay-url="/photos/12/"
 *              data-overlay-class="modal--fullscreen">Open</button>
 *
 *    `<div class="modal modal--fullscreen" id="...">` with its own header,
 *    body and footer. The response owns its size, id and chrome, so one view
 *    can return a fullscreen photo viewer and another a small dialog without
 *    this file knowing which. Right for anything with real structure: a
 *    footer of actions, a custom header, a toolbar.
 *
 *    Detected by what comes back — a response whose first element is `.modal`
 *    or `.sheet` is adopted whole (shape 3); anything else fills the body
 *    (shape 2). Nothing to declare.
 *
 * Both lazy shapes: a skeleton shows while in flight, `htmx.process()` runs on
 * the result if htmx is present, and the node is removed from the DOM on close.
 * `data-overlay-class` has to be on the TRIGGER either way, because stacking is
 * decided from `modal--fullscreen` before the response arrives.
 *
 * Why not just park expensive content in a hidden modal: a closed `.modal` is
 * `visibility: hidden`, which is still LAID OUT. Its `loading="lazy"` images
 * all fetch on page load, and anything that measures itself (maps, charts)
 * still initialises. `content-visibility: hidden` in modals.less fixes the
 * image half; only not rendering the content fixes the rest.
 *
 * Dismiss (close the nearest parent modal/sheet):
 *   <button data-dismiss="modal">Cancel</button>
 *
 * Events (both static and lazy paths, bubbling):
 *   seeme:overlay-open    — fired after .show is applied
 *   seeme:overlay-close   — fired when closing starts
 *   seeme:overlay-loaded  — lazy path only, after the response is adopted
 *
 * Programmatic:
 *   SeeMe.overlay.open(document.querySelector('#my-modal'));
 *   SeeMe.overlay.close(document.querySelector('#my-modal'));
 *   SeeMe.overlay.load('/some/url/', 'modal', { overlayClass: 'modal--lg',
 *                                               title: 'How pricing works' });
 *
 * Stacking (layer above an open sheet/modal instead of swapping it):
 *   SeeMe.overlay.open(el, { stack: true });
 *   Automatic for .modal--fullscreen, and for any modal opened while a sheet
 *   is showing (a sheet another script opened included). The stacked overlay gets its own
 *   backdrop and a higher z-index (mirrors spotlight); closing it restores
 *   the underlying overlay's backdrop and scroll lock untouched.
 */

var SeeMe = window.SeeMe || {};

SeeMe.overlay = (function () {
    var backdrop = null;
    var activeOverlay = null;
    var stacked = [];   // [{ el, backdrop, savedBodyOverflow }] — overlays layered above the active one

    function getBackdrop() {
        if (!backdrop) {
            backdrop = document.querySelector('.backdrop');
        }
        if (!backdrop) {
            backdrop = document.createElement('div');
            backdrop.className = 'backdrop';
            document.body.appendChild(backdrop);
        }
        return backdrop;
    }

    function lockScroll() {
        document.body.style.overflow = 'hidden';
    }

    function unlockScroll() {
        document.body.style.overflow = '';
    }

    function isSheet(el) {
        return el && el.classList.contains('sheet');
    }

    function open(el, opts) {
        if (!el) return;
        opts = opts || {};

        // Stack: layer above whatever is open (own backdrop, higher z-index)
        // instead of swapping it — mirrors spotlight. Fullscreen modals always
        // stack so they can be opened from inside a sheet without closing it.
        // So does any modal opened while a sheet is showing: it dims the sheet
        // instead of closing it. The DOM check, not activeOverlay, because a
        // consumer's own sheet script (RF sheets.js) opens sheets this module
        // never sees; without it the modal got the shared backdrop under the
        // sheet and tied the sheet's z-index. Sheet over sheet still swaps.
        var openSheet = document.querySelector('.sheet.show');
        if (opts.stack || el.classList.contains('modal--fullscreen')
            || (openSheet && openSheet !== el && !isSheet(el))) {
            openStacked(el);
            return;
        }

        // Swap: if a different overlay is already open, close it visually
        // without unlocking scroll (we're immediately opening another one)
        if (activeOverlay && activeOverlay !== el) {
            activeOverlay.classList.remove('show');
            activeOverlay = null;
        }

        var bd = getBackdrop();

        // Sheets get a faded backdrop so page content stays visible
        bd.classList.toggle('backdrop--faded', isSheet(el));

        // Force reflow so transitions fire
        void bd.offsetHeight;
        bd.classList.add('show');

        void el.offsetHeight;
        el.classList.add('show');

        activeOverlay = el;
        lockScroll();
        emit(el, 'seeme:overlay-open');
    }

    // Lifecycle events. Consumers need a hook that fires for BOTH the static
    // and the lazy path — content that initialises on open (maps, charts,
    // anything that measures) can't use a load event, because a reopened
    // static overlay never loads anything.
    function emit(el, name) {
        el.dispatchEvent(new CustomEvent(name, { bubbles: true }));
    }

    function openStacked(el) {
        for (var i = 0; i < stacked.length; i++) {
            if (stacked[i].el === el) return;   // already open
        }

        // Dedicated backdrop — the shared one stays owned by the overlay underneath
        var bd = document.createElement('div');
        bd.className = 'backdrop backdrop--stacked';
        document.body.appendChild(bd);
        bd.addEventListener('click', function (e) {
            e.stopPropagation();
            close(el);
        });

        void bd.offsetHeight;
        bd.classList.add('show');

        el.classList.add('modal--stacked');
        void el.offsetHeight;
        el.classList.add('show');

        stacked.push({ el: el, backdrop: bd, savedBodyOverflow: document.body.style.overflow });
        document.body.style.overflow = 'hidden';
        emit(el, 'seeme:overlay-open');
    }

    function closeStacked(entry) {
        entry.el.classList.remove('show');
        // Keep the elevated z-index (.modal--stacked) through the fade-out —
        // dropping it immediately would let the overlay underneath (same
        // z-band, later in DOM) cover the closing modal mid-transition.
        entry.el.addEventListener('transitionend', function handler() {
            entry.el.removeEventListener('transitionend', handler);
            entry.el.classList.remove('modal--stacked');
        });
        entry.backdrop.classList.remove('show');
        entry.backdrop.addEventListener('transitionend', function handler() {
            entry.backdrop.removeEventListener('transitionend', handler);
            if (entry.backdrop.parentNode) entry.backdrop.parentNode.removeChild(entry.backdrop);
        });
        // Restore the underlying overlay's scroll lock — but only if something
        // is still open underneath. The saved value can be stale: the sheet may
        // have been closed out from under us (history nav, HTMX boosted nav),
        // and blindly restoring 'hidden' would leave the page scroll-locked
        // with nothing on screen.
        var stillOpen = stacked.length || activeOverlay ||
            document.querySelector('.sheet.show, .modal.show');
        document.body.style.overflow = stillOpen ? (entry.savedBodyOverflow || 'hidden') : '';
        emit(entry.el, 'seeme:overlay-close');
        removeIfDynamic(entry.el);
    }

    function close(el) {
        el = el || (stacked.length ? stacked[stacked.length - 1].el : activeOverlay);
        if (!el) return;

        // Stacked overlays own their backdrop and scroll state — closing one
        // leaves the overlay underneath (sheet/modal) fully intact.
        for (var i = 0; i < stacked.length; i++) {
            if (stacked[i].el === el) {
                closeStacked(stacked.splice(i, 1)[0]);
                return;
            }
        }

        el.classList.remove('show');
        var bd = getBackdrop();
        // If a sheet (managed by sheets.js, not by us) is still visible
        // underneath, leave the backdrop in its sheet-faded state instead of
        // clearing it — otherwise the backdrop becomes click-inert and the
        // sheet can no longer be dismissed by clicking outside.
        var underlyingSheet = document.querySelector('.sheet.show');
        if (underlyingSheet && underlyingSheet !== el) {
            bd.classList.add('backdrop--faded');
            // Keep .show + scroll lock — the sheet still owns them.
        } else {
            bd.classList.remove('show');
            bd.classList.remove('backdrop--faded');
            unlockScroll();
        }

        activeOverlay = null;
        emit(el, 'seeme:overlay-close');
        removeIfDynamic(el);
    }

    // Remove dynamically loaded overlays after their close transition
    function removeIfDynamic(el) {
        if (!el.hasAttribute('data-overlay-dynamic')) return;
        el.addEventListener('transitionend', function handler() {
            el.removeEventListener('transitionend', handler);
            if (el.parentNode) el.parentNode.removeChild(el);
        });
    }

    // ====================================================================
    // Lazy overlays — the server renders the content
    // ====================================================================
    // `load()` opens a placeholder immediately, then fits the response to it.
    // A response whose first element is `.modal` / `.sheet` is adopted WHOLE,
    // so it owns its own size class, header, footer and id — one view returns
    // a fullscreen photo viewer, another a small dialog, and this file never
    // knows which. Anything else is treated as a body fragment and poured into
    // the placeholder's shell, which is the cheap shape for help text.
    //
    // Prefer this over parking real content inside a closed modal. A closed
    // modal is `visibility: hidden`, which is still LAID OUT — its images
    // fetch on page load and any JS that measures (maps, charts) still
    // initialises. `content-visibility: hidden` (modals.less) stops the image
    // half; only not rendering the content stops the rest.
    //
    // Because the response's classes are not known until it arrives, the
    // trigger declares them up front with `data-overlay-class` — `open()`
    // decides stacking from `modal--fullscreen`, and that decision has to be
    // made before the first byte lands.

    function escapeHtml(str) {
        var div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }

    // The placeholder shell. It carries a real header with a close button, not
    // just a body — a FRAGMENT response never replaces this chrome (it only
    // fills the body), so without a close button here a fragment-backed overlay
    // would be dismissable by Escape and backdrop click only.
    function buildShell(type, titleHtml, bodyHtml) {
        var header =
            '<div class="' + type + '__header">' + titleHtml +
                '<button class="' + type + '__close" type="button" data-dismiss="modal">' +
                    '<i class="fa-solid fa-xmark"></i></button>' +
            '</div>';
        if (type === 'modal') {
            return '<div class="modal__dialog">' + header +
                       '<div class="modal__body">' + bodyHtml + '</div>' +
                   '</div>';
        }
        return '<div class="sheet__handle"></div>' + header +
               '<div class="sheet__body">' + bodyHtml + '</div>';
    }

    function buildSkeleton(type, title) {
        // A skeleton, not a spinner: what is arriving is a whole overlay of
        // content, and a skeleton reads as "this shape is coming" where a
        // spinner only says "something is happening".
        //
        // With no title the heading gets a skeleton bar too, so the header
        // doesn't sit empty next to a lone X while the response is in flight.
        var titleHtml = title
            ? '<h3>' + escapeHtml(title) + '</h3>'
            : '<div class="skeleton skeleton--heading mb-0"></div>';
        var lines =
            '<div class="skeleton skeleton--text"></div>' +
            '<div class="skeleton skeleton--text"></div>' +
            '<div class="skeleton skeleton--short"></div>';
        return buildShell(type, titleHtml, lines);
    }

    function buildError(type) {
        return buildShell(
            type,
            '<h3>Something went wrong</h3>',
            '<p class="text-muted">Could not load this content. Close and try again.</p>'
        );
    }

    // Adopt a server-rendered overlay into the placeholder that is already
    // open. The node itself is kept and its attributes/children replaced,
    // rather than swapping in the response node — `stacked[]`, `activeOverlay`
    // and the caller's reference all point at this element already, and
    // repointing three bits of bookkeeping mid-transition is how you end up
    // with a stuck backdrop.
    function adopt(el, html, type) {
        var tpl = document.createElement('template');
        tpl.innerHTML = html.trim();
        var incoming = tpl.content.firstElementChild;

        var isOverlayRoot = incoming && (
            incoming.classList.contains('modal') || incoming.classList.contains('sheet')
        );
        if (!isOverlayRoot) {
            // FRAGMENT response — body-level content, not a whole overlay.
            // Keep the placeholder's shell (header, close button, size class
            // from `data-overlay-class`) and fill only the body. The right
            // shape for short static content: help text, a policy blurb, a
            // definition. Give it a heading with `data-overlay-title`.
            var body = el.querySelector('.modal__body, .sheet__body');
            if (body) body.innerHTML = html;
            // Untitled: swap the heading's loading bar for an empty h3 rather
            // than removing it. Left shimmering it reads as "still loading",
            // and removing it outright drags the close button to the left of a
            // `space-between` header.
            var pending = el.querySelector('.modal__header .skeleton, .sheet__header .skeleton');
            if (pending) pending.parentNode.replaceChild(document.createElement('h3'), pending);
            return;
        }

        // Carry over the live open state; everything else comes from the response.
        var live = [];
        ['show', 'modal--stacked'].forEach(function (cls) {
            if (el.classList.contains(cls)) live.push(cls);
        });

        for (var i = el.attributes.length - 1; i >= 0; i--) {
            el.removeAttribute(el.attributes[i].name);
        }
        for (var j = 0; j < incoming.attributes.length; j++) {
            el.setAttribute(incoming.attributes[j].name, incoming.attributes[j].value);
        }
        el.classList.add(type);
        live.forEach(function (cls) { el.classList.add(cls); });
        el.setAttribute('data-overlay-dynamic', '');

        el.innerHTML = incoming.innerHTML;
    }

    function load(url, type, opts) {
        type = type || 'modal';
        opts = opts || {};

        var el = document.createElement('div');
        el.className = type + (opts.overlayClass ? ' ' + opts.overlayClass : '');
        el.setAttribute('data-overlay-dynamic', '');
        el.setAttribute('data-overlay-loading', '');
        el.innerHTML = buildSkeleton(type, opts.title || '');

        document.body.appendChild(el);
        open(el, opts);

        var xhr = new XMLHttpRequest();
        xhr.open('GET', url);
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        xhr.onload = function () {
            el.removeAttribute('data-overlay-loading');
            if (xhr.status >= 200 && xhr.status < 300) {
                adopt(el, xhr.responseText, type);
                // Bind whatever framework the consuming app uses to the content
                // that just arrived. Soft dependency, same as swipe-action.js
                // and confirm-delete.js.
                if (window.htmx) window.htmx.process(el);
                el.dispatchEvent(new CustomEvent('seeme:overlay-loaded', {
                    bubbles: true, detail: { url: url },
                }));
            } else {
                el.innerHTML = buildError(type);
            }
        };
        xhr.onerror = function () {
            el.removeAttribute('data-overlay-loading');
            el.innerHTML = buildError(type);
        };
        xhr.send();

        return el;
    }

    // Close on Escape — topmost stacked overlay first
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && (stacked.length || activeOverlay)) {
            close();
        }
    });

    // Close on backdrop click
    document.addEventListener('click', function (e) {
        if (e.target.classList.contains('backdrop') && activeOverlay) {
            close();
        }
        // Also close when clicking the modal container (outside dialog)
        if (e.target.classList.contains('modal') && e.target.classList.contains('show')) {
            close(e.target);
        }
    });

    // Auto-bind data attributes
    document.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-overlay-target], [data-overlay-url]');
        if (!trigger) return;

        e.preventDefault();

        var target = trigger.getAttribute('data-overlay-target');
        var url = trigger.getAttribute('data-overlay-url');
        var type = trigger.getAttribute('data-overlay-type') || 'modal';

        if (target) {
            var el = document.querySelector(target);
            if (el) open(el);
        } else if (url) {
            // Guard the double-click. Without it an impatient second click
            // stacks a second copy of the same overlay, and closing one leaves
            // the other stranded over a locked-scroll page.
            if (trigger.hasAttribute('data-overlay-pending')) return;
            trigger.setAttribute('data-overlay-pending', '');

            var overlay = load(url, type, {
                overlayClass: trigger.getAttribute('data-overlay-class') || '',
                title: trigger.getAttribute('data-overlay-title') || '',
            });
            overlay.addEventListener('seeme:overlay-close', function () {
                trigger.removeAttribute('data-overlay-pending');
            });
        }
    });

    // Dismiss via [data-dismiss="modal"] — closes the nearest parent .modal or .sheet
    document.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-dismiss="modal"]');
        if (!trigger) return;
        var overlay = trigger.closest('.modal, .sheet');
        if (overlay) close(overlay);
    });

    // Close buttons, bound by delegation rather than at DOMContentLoaded —
    // a one-shot pass over the document misses every overlay that arrives
    // later, which is now the normal case (`data-overlay-url`, HTMX swaps).
    document.addEventListener('click', function (e) {
        var btn = e.target.closest('.modal__close, .sheet__close');
        if (!btn) return;
        if (btn.hasAttribute('data-dismiss')) return;   // handled above
        var overlay = btn.closest('.modal, .sheet');
        if (overlay) close(overlay);
    });

    return { open: open, close: close, load: load };
})();

window.SeeMe = SeeMe;
